import React, { useState, useMemo } from 'react';
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  notification,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { TranslationOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import useFetch from '../../../../../hooks/useFetch';
import { FetchApiInterface } from '../../../../../interfaces/FetchApi.interface';
import { AxiosResponse } from 'axios';
import { ResponseInterface } from '../../../../../interfaces/Response.interface';
import { USER_INFO } from '../../../../../config/config';

const { TextArea } = Input;
const { Text } = Typography;

const VALID_STATUSES = ['RECEIVED_LATE', 'RECEIVED_ON_TIME', 'OBSERVED', 'OBSERVATION_SENT', 'FAILED', 'RECOVERED'];

interface RequestTranslationProps {
  selectedItems: Array<any>;
  selectedRows: Array<any>;
}

const RequestTranslation: React.FC<RequestTranslationProps> = ({ selectedItems, selectedRows }) => {
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedTranslatorKey, setSelectedTranslatorKey] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<any>({});
  const [translators, setTranslators] = useState<Array<any>>([]);
  const [form] = Form.useForm();
  const [loadingRecover, setLoadingRecover] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [translatorSearch, setTranslatorSearch] = useState('');
  const [tableView, setTableView] = useState<'valid' | 'assigned' | 'excluded'>('valid');
  const { t } = useTranslation();
  const { fetchApi } = useFetch();

  const getTranslators = (all = false) => {
    const userInfo = JSON.parse(localStorage.getItem(USER_INFO) || '{}');
    const dpaIdCountry = userInfo?.user?.dpa?.dpaIdCountry;
    const countryParam = dpaIdCountry ? `dpaCountryId=${dpaIdCountry}` : '';
    const query = all
      ? `?all=true${countryParam ? `&${countryParam}` : ''}`
      : (countryParam ? `?${countryParam}` : '');
    const dataSend: FetchApiInterface = {
      url: `/users/get-translators${query}`,
      method: 'GET',
    };
    fetchApi(dataSend).then(
      ({ data }: AxiosResponse<ResponseInterface>) => {
        if (data.status === 'success') {
          setTranslators(data?.data || []);
        }
      }
    ).catch(() => { });
  };

  const selectedTranslator = translators.find((tr) => tr.useId === selectedTranslatorKey);

  const filteredTranslators = useMemo(() => {
    const q = translatorSearch.toLowerCase().trim();
    if (!q) return translators;
    return translators.filter(tr =>
      `${tr.useName} ${tr.useLastName}`.toLowerCase().includes(q) ||
      tr.useEmail?.toLowerCase().includes(q) ||
      tr.dpaName?.toLowerCase().includes(q)
    );
  }, [translators, translatorSearch]);

  const validItems = useMemo(() => {
    const direction = formValues.translationType2;
    return selectedRows.filter((item) => {
      if (!VALID_STATUSES.includes(item.status)) return false;
      if (direction === 'SC-SP') return item.assComType !== 'PDF';
      if (direction === 'SP-SC') return item.assComType === 'PDF';
      return true;
    });
  }, [selectedRows, formValues]);

  const invalidCount = selectedRows.length - validItems.length;

  const assignedItems = useMemo(
    () => selectedRows.filter((r: any) => r.userAssignedName),
    [selectedRows]
  );

  const excludedItems = useMemo(
    () => selectedRows.filter((r: any) => !VALID_STATUSES.includes(r.status)),
    [selectedRows]
  );

  const infoColumns = [
    { title: t('nro'), key: 'nro', width: 50, render: (_: any, __: any, i: number) => i + 1 },
    { title: t('sc'), dataIndex: 'ascScNumber', key: 'ascScNumber', width: 100 },
    { title: t('scName'), dataIndex: 'ascScName', key: 'ascScName' },
    { title: t('type'), dataIndex: 'assComType', key: 'assComType', width: 80 },
    { title: t('status'), dataIndex: 'status', key: 'status', width: 160 },
    {
      title: t('assignedTranslator'),
      key: 'assignedTranslator',
      render: (_: any, r: any) =>
        r.userAssignedName ? `${r.userAssignedName} ${r.userAssignedLastname || ''}`.trim() : '-',
    },
  ];

  const translatorColumns = [
    {
      title: '',
      key: 'select',
      width: 40,
      render: (_: any, record: any) => (
        <Radio
          checked={selectedTranslatorKey === record.useId}
          onChange={() => {
            setSelectedTranslatorKey(record.useId);
            form.resetFields(['targetLanguage']);
          }}
        />
      ),
    },
    {
      title: t('translator'),
      key: 'name',
      width: 160,
      render: (_: any, record: any) => `${record.useName} ${record.useLastName}`,
    },
    { title: t('email'),            dataIndex: 'useEmail',          key: 'useEmail',          width: 200 },
    { title: t('country'),          dataIndex: 'dpaName',           key: 'dpaName',           width: 110 },
    { title: t('currentWorkload'),  dataIndex: 'activeWorkload',    key: 'activeWorkload',    width: 110 },
    { title: t('monthlyTranslations'), dataIndex: 'currentMonthTotal', key: 'currentMonthTotal', width: 150 },
    {
      title: t('languages'),
      key: 'languages',
      width: 200,
      render: (_: any, record: any) =>
        record.translatorLanguages?.map((l: any) => l.tlaName).join(' / ') || '-',
    },
  ];

  const communicationColumns = [
    { title: t('nro'), key: 'nro', render: (_: any, __: any, index: number) => index + 1 },
    { title: t('sc'), dataIndex: 'ascScNumber', key: 'ascScNumber' },
    { title: t('scName'), dataIndex: 'ascScName', key: 'ascScName' },
    { title: t('requestedBy'), dataIndex: 'lastModifiedBy', key: 'lastModifiedBy' },
    {
      title: t('translator'),
      key: 'translator',
      render: () => selectedTranslator
        ? `${selectedTranslator.useName} ${selectedTranslator.useLastName}`
        : '-',
    },
    {
      title: t('targetLanguageLabel'),
      key: 'targetLanguage',
      render: () => formValues.targetLanguage || '-',
    },
    {
      title: t('deliveryDateLabel'),
      key: 'deliveryDate',
      render: () =>
        formValues.deliveryDate ? moment(formValues.deliveryDate).format('DD-MM-YYYY') : '-',
    },
  ];

  const handleOpen = () => {
    setSelectedTranslatorKey(null);
    setTableView('valid');
    form.resetFields();

    const allPdf = selectedRows.length > 0 && selectedRows.every((r: any) => r.assComType === 'PDF');
    const initialValues: any = {
      translatorType: 'country',
      requestDate: moment(),
      ...(allPdf ? { translationType2: 'SP-SC' } : {}),
    };

    form.setFieldsValue(initialValues);
    setFormValues(allPdf ? { translationType2: 'SP-SC' } : {});
    getTranslators();
    setOpen(true);
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      setFormValues(values);
      setOpen(false);
      setConfirmOpen(true);
    });
  };

  const handleConfirm = () => {
    const userInfo = JSON.parse(localStorage.getItem(USER_INFO) || '{}');
    const requestedByUserId = userInfo?.user?.useId;

    const targetLang = selectedTranslator?.translatorLanguages?.find(
      (l: any) => l.tlaName === formValues.targetLanguage
    );

    const directionMap: Record<string, string> = {
      'SC-SP': 'SC_TO_SP',
      'SP-SC': 'SP_TO_SC',
    };

    const payload = {
      requestedByUserId: String(requestedByUserId),
      assignedTranslatorId: String(selectedTranslator?.useId),
      // TODO: eliminar sourceLanguageId cuando backend no lo requiera
      sourceLanguageId: 1,
      targetLanguageId: targetLang?.tlaId,
      translationDirection: directionMap[formValues.translationType2] ?? formValues.translationType2,
      dueDate: formValues.deliveryDate
        ? moment(formValues.deliveryDate).format('YYYY-MM-DDTHH:mm:ss[Z]')
        : null,
      translatorNotes: formValues.comments || null,
      createdBy: '',
      communications: validItems.map((item: any) => ({
        assignedId: item.assId,
        referenceNumber: String(item.assRef),
        fileNames: [item.conImage, item.conImageOptional].filter(Boolean),
      })),
    };

    const dataSend: FetchApiInterface = {
      url: '/translations/create',
      method: 'POST',
      data: payload,
    };

    setLoadingConfirm(true);
    fetchApi(dataSend).then(
      ({ data }: AxiosResponse<any>) => {
        if (Array.isArray(data) || data?.status === 'success') {
          setConfirmOpen(false);
        }
      }
    ).catch((err: any) => {
      const detail = err?.detail || t('errorCreatingTranslationRequest');
      notification.error({ message: t('error'), description: detail });
    }).finally(() => setLoadingConfirm(false));
  };

  const resendIds = useMemo(
    () => selectedRows.filter((r: any) => r.userAssignedName).map((r: any) => r.assId),
    [selectedRows]
  );

  const recoverIds = useMemo(
    () => selectedRows
      .filter((r: any) => r.userAssignedName && (r.translationId ?? r.requestId))
      .map((r: any) => r.translationId ?? r.requestId),
    [selectedRows]
  );

  const handleRecover = () => {
    setLoadingRecover(true);
    fetchApi({ url: '/translations/recover', method: 'PATCH', data: { requestIds: recoverIds } })
      .then(() => { notification.success({ message: t('recoverSentSuccessfully') }); })
      .catch((err: any) => { notification.error({ message: t('error'), description: err?.message || t('errorRecoveringSend') }); })
      .finally(() => setLoadingRecover(false));
  };

  const handleResend = () => {
    setLoadingResend(true);
    fetchApi({ url: '/translations/resend', method: 'PATCH', data: { assignedIds: resendIds } })
      .then(() => { notification.success({ message: t('resendSuccessfully') }); })
      .catch((err: any) => { notification.error({ message: t('error'), description: err?.message || t('errorResending') }); })
      .finally(() => setLoadingResend(false));
  };

  const isSubmitDisabled =
    validItems.length === 0 ||
    !selectedTranslatorKey ||
    !formValues.translationType2 ||
    !formValues.targetLanguage ||
    !formValues.deliveryDate;

  const footer = (
    <Space wrap>
      <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
      <Button loading={loadingRecover} onClick={handleRecover} disabled={recoverIds.length === 0}>
        {t('recoverSend')} ({recoverIds.length})
      </Button>
      <Button loading={loadingResend} onClick={handleResend} disabled={resendIds.length === 0}>
        {t('resend')} ({resendIds.length})
      </Button>
      <Tooltip title={isSubmitDisabled ? t('completeAllRequiredFields') : undefined}>
        <Button type='primary' icon={<TranslationOutlined />} onClick={handleSubmit} disabled={isSubmitDisabled}>
          {t('translateWithAIAndSend')}
        </Button>
      </Tooltip>
    </Space>
  );

  const alertMessage = () => {
    if (formValues.translationType2 === 'SC-SP') return t('commExcludedScSp', { count: invalidCount });
    if (formValues.translationType2 === 'SP-SC') return t('commExcludedSpSc', { count: invalidCount });
    return t('commExcludedStatus', { count: invalidCount });
  };

  return (
    <>
      <Tooltip title={t('requestTranslation')}>
        <Button
          type='primary'
          disabled={selectedItems.length <= 0}
          icon={<TranslationOutlined />}
          onClick={handleOpen}
        />
      </Tooltip>

      <Modal
        centered
        open={open}
        title={t('requestTranslation')}
        onCancel={() => setOpen(false)}
        width={960}
        footer={footer}
      >
        {invalidCount > 0 && (
          <Alert
            type='warning'
            showIcon
            message={alertMessage()}
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          form={form}
          layout='vertical'
          onValuesChange={(changed, all) => {
            setFormValues(all);
            if ('translatorType' in changed) {
              setSelectedTranslatorKey(null);
              form.resetFields(['targetLanguage']);
              getTranslators(changed.translatorType === 'other');
            }
          }}
        >
          <Form.Item label={t('translatorTypeLabel')} name='translatorType'>
            <Select style={{ width: 220 }}>
              <Select.Option value='country'>{t('countryTranslator')}</Select.Option>
              <Select.Option value='other'>{t('otherCountryTranslator')}</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label={t('availableTranslators')} required>
            <Input.Search
              placeholder={t('searchByNameEmailCountry')}
              allowClear
              style={{ marginBottom: 8 }}
              onChange={e => setTranslatorSearch(e.target.value)}
            />
            <Table
              size='small'
              dataSource={filteredTranslators}
              columns={translatorColumns}
              pagination={false}
              rowKey='useId'
              scroll={{ x: 'max-content', y: 200 }}
            />
          </Form.Item>

          <Form.Item
            label={t('translationType')}
            name='translationType2'
            rules={[{ required: true, message: t('selectTranslationType') }]}
          >
            <Radio.Group>
              <Radio value='SC-SP'>SC-SP</Radio>
              <Radio value='SP-SC'>SP-SC</Radio>
            </Radio.Group>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('finalLanguage')}
                name='targetLanguage'
                rules={[{ required: true, message: t('selectFinalLanguage') }]}
              >
                <Select disabled={!selectedTranslator}>
                  {(selectedTranslator?.translatorLanguages || []).map((l: any) => (
                    <Select.Option key={l.tlaId} value={l.tlaName}>{l.tlaName}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('requestDateLabel')} name='requestDate'>
                <DatePicker disabled format='DD/MM/YYYY' style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('deliveryDateLabel')}
                name='deliveryDate'
                rules={[{ required: true, message: t('selectDeliveryDate') }]}
              >
                <DatePicker
                  format='DD/MM/YYYY'
                  style={{ width: '100%' }}
                  disabledDate={(current) => current && current <= moment().endOf('day')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={t('commentsForTranslator')} name='comments'>
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item label={t('communicationList')}>
            <Radio.Group
              value={tableView}
              onChange={e => setTableView(e.target.value)}
              optionType='button'
              buttonStyle='solid'
              size='small'
              style={{ marginBottom: 8 }}
            >
              <Radio.Button value='valid'>{t('forTranslation')} ({validItems.length})</Radio.Button>
              <Radio.Button value='assigned'>{t('withTranslatorTab')} ({assignedItems.length})</Radio.Button>
              <Radio.Button value='excluded'>{t('excludedTab')} ({excludedItems.length})</Radio.Button>
            </Radio.Group>
            <Table
              size='small'
              dataSource={tableView === 'valid' ? validItems : tableView === 'assigned' ? assignedItems : excludedItems}
              columns={tableView === 'valid' ? communicationColumns : infoColumns}
              pagination={false}
              rowKey='assId'
              locale={{ emptyText: t('noCommunicationsInCategory') }}
              footer={tableView === 'valid' && validItems.length > 0 ? () => (
                <Text strong>{t('totalCommunicationsToTranslate', { count: validItems.length })}</Text>
              ) : undefined}
              scroll={{ y: 200 }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        centered
        open={confirmOpen}
        title={t('confirmRequest')}
        onCancel={() => setConfirmOpen(false)}
        footer={
          <Space>
            <Button onClick={() => { setConfirmOpen(false); setOpen(true); }}>{t('back')}</Button>
            <Button type='primary' loading={loadingConfirm} onClick={handleConfirm}>{t('accept')}</Button>
          </Space>
        }
      >
        <p>{t('translationRequestWillBeSent')}</p>
        <div style={{ border: '1px solid #1677ff', borderRadius: 4, padding: '12px 16px', marginBottom: 16 }}>
          <p style={{ margin: '4px 0' }}><strong>{t('translator')}:</strong> {selectedTranslator ? `${selectedTranslator.useName} ${selectedTranslator.useLastName}` : t('unselected')}</p>
          <p style={{ margin: '4px 0' }}><strong>{t('finalLanguage')}:</strong> {formValues.targetLanguage || '-'}</p>
          <p style={{ margin: '4px 0' }}><strong>{t('totalCommunications')}:</strong> {validItems.length}</p>
          <p style={{ margin: '4px 0' }}><strong>{t('requestDateLabel')}:</strong> {moment().format('DD/MM/YYYY')}</p>
          <p style={{ margin: '4px 0' }}><strong>{t('deliveryDateLabel')}:</strong> {formValues.deliveryDate ? moment(formValues.deliveryDate).format('DD/MM/YYYY') : '-'}</p>
          {formValues.comments && (
            <p style={{ margin: '4px 0' }}><strong>{t('commentsForTranslator')}:</strong> {formValues.comments}</p>
          )}
        </div>
        <p>{t('confirmActionQuestion')}</p>
      </Modal>
    </>
  );
};

export default RequestTranslation;
